// ==========================================
// ENUMS
// ==========================================

import type { PaymentStatus } from "./payment";

// Estado de la Orden
export const StatusOrder = {
	PENDING: "Dialogando",
	WAITING: "En_Espera",
	IN_PROGRESS: "En_Progreso",
	SUPPORT: "Soporte",
	COMPLETED: "Completado",
	WARRANTY: "Garantia",
	CANCELLED: "Cancelado",
	PAID: "Pagado",
} as const;

export type StatusOrder = (typeof StatusOrder)[keyof typeof StatusOrder];

// ==========================================
// SUB-TYPES (Detalles anidados)
// ==========================================

// Usuario genérico (Cliente o Proveedor)
export interface OrderUserSummary {
	id?: number;
	names: string;
	last_names: string;
	email: string;
	phone: string;
	image_profile?: string | null;
}

// Cliente
export interface OrderClientSummary {
	id: number;
	user_id?: number;
	users: OrderUserSummary;
}

// Proveedor - Categorías (Estructura simple en el objeto provider)
export interface ProviderCategorySimple {
	categories_services: {
		name_category: string;
	};
}

// Proveedor
export interface OrderProviderSummary {
	id: number;
	company_name: string;
	users: OrderUserSummary;
	categories_providers: ProviderCategorySimple[];
}

// --- Cotizaciones (Estructura Profunda) ---

// Categoría más profunda (dentro de la request)
export interface ServiceCategoryDetail {
	id: number;
	name_category: string;
	icon_category?: string;
}

// Wrapper de la categoría en la request
export interface ServiceCategoryWrapper {
	categories_providers: {
		categories_services: ServiceCategoryDetail;
	};
}

// Servicio del proveedor
export interface ServiceProviderDetail {
	id: number;
	name_service: string;
	description_service: string;
	base_price?: number;
	categories_services_providers: ServiceCategoryWrapper[];
}

// Solicitud (Request)
export interface RequestDetail {
	id: number;
	title_request: string;
	description_request: string;
	date_request?: string | null;
	hour_request?: string | null;
	proposed_price_request?: number;
	services_providers: ServiceProviderDetail;
}

// Item individual de la cotización (NUEVO)
export interface QuotationItem {
	id: number;
	quotation_id: number;
	description_item: string;
	picture_item: string | null;
	subtotal_item: number;
	created_at: string;
	updated_at: string;
}

// Cotización
export interface OrderQuotationSummary {
	id: number;
	proposed_price: number;
	status_quotation: boolean;
	date_accepted?: string | null;
	date_rejected?: string | null;
	requests?: RequestDetail;
	quotation_items: QuotationItem[]; // <--- Agregado el arreglo de items
}

// Dirección
export interface OrderAddressSummary {
	id: number;
	street_address: string;
	reference_address: string;
}

// Pagos
export interface OrderPayment {
	id: number;
	order_id?: number;
	method_id?: number;
	mehod_id?: number;
	amount_payment: number;
	rate_payment: number;
	from_account: string;
	to_account: string;
	payment_method?: string;
	methods?: {
		id: number;
		name_method: string;
		currency?: string;
		is_active?: boolean;
	} | null;
	payment_support: string;
	date_payment: string;
	payment_status: PaymentStatus;
	date_approval_payment: string | null;
	date_rejection_payment: string | null;
	rejection_reason_payment: string | null;
}

// ==========================================
// RESPONSE TYPE (Principal)
// ==========================================
export type OrderPaginatedResponse<T> = {
	items: T[];
	meta: {
		pagination: {
			totalItems: number;
			page: number;
			pageSize: number;
			totalPages: number;
		};
	};
};

export interface OrderResponse {
	type: string; // "order"
	id: number;
	code_order: string;
	status_order: StatusOrder;
	order_price: number;
	order_price_aux?: number | null;
	client_id?: number | null;
	request_id?: number | null;
	// Fechas de Control (Strings ISO)
	date_initial_order: string;
	date_waiting_order: string | null;
	date_in_progress_order: string | null;
	date_completion_order: string | null;
	date_cancelation_order: string | null;
	date_guarantee_order: string | null;
	date_support_order: string | null;
	date_paid_order?: string | null;
	estimated_date_completion: string;

	// Garantía
	date_expiration_guarantee: string | null;
	is_guarantee_used: boolean;

	// Razones y Estados
	status_reason: string | null;
	reason_culmination: string | null;
	cancelation_reason: string | null;
	is_completed: boolean;
	is_canceled: boolean;

	// Relaciones
	payments: OrderPayment[];
	clients: OrderClientSummary;
	providers: OrderProviderSummary;
	quotations: OrderQuotationSummary[]; // <--- Cambiado de Objeto a Arreglo
	addresses: OrderAddressSummary;
	requests?: RequestDetail;

	created_at?: string;
}

// ==========================================
// PAYLOAD TYPES
// ==========================================

export interface CreateOrderPayload {
	quotation_id?: number;
	client_id: number;
	provider_id: number;
	address_id: number;
	request_id: number;
	order_price?: number;
	order_price_aux: number;
	estimated_date_completion: Date | string;
}

export interface RejectionOrderPayload {
	cancelation_reason: string;
}

export interface WarrantyOrderPayload {
	is_guarantee_used: boolean;
	date_expiration_guarantee: Date | string;
}

export interface UpdateStatusOrderPayload {
	status: StatusOrder;
	reason?: string;
}

export interface SendOrderPaymentReceiptPayload {
	provider_email: string;
	observations?: string;
}

export interface SendOrderPaymentReceiptResponse {
	type?: string;
	id?: number;
	success: boolean;
	message: string;
	order_id: number;
	provider_email: string;
	receipt_image: string | null;
	observations: string | null;
}
